import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, CheckCircle, Clock, Award, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const pricingData = [
  { course: 'Heartsaver CPR/AED', price: '$77' },
  { course: 'Heartsaver First Aid', price: '$77' },
  { course: 'CPR/AED & First Aid', price: '$115' },
  { course: 'BLS for Healthcare Providers', price: '$85' },
  { course: 'BLS AND First Aid', price: '$135' },
  { course: 'Friends and Family', price: '$30' },
];

const whatYouLearn = [
  'Adult, child, and infant CPR techniques',
  'How to use an AED (Automated External Defibrillator)',
  'Choking relief for all ages',
  'First aid for bleeding, burns, and injuries',
  'Recognizing heart attack and stroke symptoms',
  'Emergency scene assessment',
];

const whoItsFor = [
  'Parents and caregivers',
  'Teachers and school staff',
  'Fitness instructors',
  'Childcare providers',
  'Workplace safety requirements',
  'Anyone who wants to be prepared',
];

export default function CPRTraining() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#EDF6F9] via-white to-[#83C5BE]/20 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              <span>One-on-One Training</span>
            </div>
            <h1 
              className="text-4xl md:text-5xl font-bold text-[#264653] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="cpr-training-title"
            >
              CPR & First Aid Training
            </h1>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Professional, personalized CPR and First Aid certification courses. 
              We come to you and work around your schedule.
            </p>
            <Link to="/book-now">
              <Button 
                size="lg" 
                className="rounded-full bg-primary hover:bg-primary/90 px-8 py-6 text-lg"
                data-testid="book-cpr-training-btn"
              >
                Book CPR Training
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* What You'll Learn */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full border-slate-100" data-testid="what-you-learn-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-2xl text-[#264653]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Award className="w-5 h-5 text-primary" />
                    </div>
                    What You'll Learn
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {whatYouLearn.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* Who It's For */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="h-full border-slate-100" data-testid="who-its-for-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-2xl text-[#264653]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <div className="w-10 h-10 rounded-lg bg-[#E29578]/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#E29578]" />
                    </div>
                    Who It's For
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4">
                    {whoItsFor.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#E29578] flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <section className="py-16 md:py-24 bg-[#EDF6F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#264653] mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              One-on-One Pricing
            </h2>
            <p className="text-muted-foreground">Clear, upfront pricing for all our training courses</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="max-w-2xl mx-auto border-slate-100 overflow-hidden" data-testid="pricing-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary/5">
                    <TableHead className="text-[#264653] font-semibold text-base">Course</TableHead>
                    <TableHead className="text-right text-[#264653] font-semibold text-base">Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pricingData.map((item, index) => (
                    <TableRow key={index} className="hover:bg-primary/5">
                      <TableCell className="font-medium text-[#264653]">{item.course}</TableCell>
                      <TableCell className="text-right text-primary font-bold text-lg">{item.price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </motion.div>
          <div className="text-center mt-10">
            <Link to="/book-now">
              <Button 
                size="lg" 
                className="rounded-full bg-primary hover:bg-primary/90 px-8 py-6 text-lg"
                data-testid="book-now-from-pricing-btn"
              >
                Book CPR Training
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="text-center p-6 border-slate-100" data-testid="info-card-duration">
              <CardContent className="pt-4">
                <Clock className="w-10 h-10 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-[#264653] text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Class Duration
                </h3>
                <p className="text-muted-foreground">2-3 hours depending on course</p>
              </CardContent>
            </Card>
            <Card className="text-center p-6 border-slate-100" data-testid="info-card-certification">
              <CardContent className="pt-4">
                <Award className="w-10 h-10 text-[#83C5BE] mx-auto mb-4" />
                <h3 className="font-semibold text-[#264653] text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Certification
                </h3>
                <p className="text-muted-foreground">American Heart Association certified</p>
              </CardContent>
            </Card>
            <Card className="text-center p-6 border-slate-100" data-testid="info-card-mobile">
              <CardContent className="pt-4">
                <Heart className="w-10 h-10 text-[#E29578] mx-auto mb-4" />
                <h3 className="font-semibold text-[#264653] text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Mobile Service
                </h3>
                <p className="text-muted-foreground">We come to your location</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
